import { useState } from "react";
import {
  Bell,
  CheckCheck,
  Loader2,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { keepPreviousData } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { useSetPageHeader } from "@/lib/page-header";
import { useNotificationStream } from "@/lib/notification-stream";
import { useAbrirNotificacao } from "@/lib/abrir-notificacao";
import { cn } from "@/lib/cn";
import { iconFor } from "@/components/notifications/notification-icons";
import { useConfirm } from "@/lib/confirm";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { NOTIFICACOES_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { FilterBar } from "@/components/shared/FilterBar";

type Filter = "all" | "unread";

const PAGE_SIZE = 30;

export function Notifications() {
  const confirm = useConfirm();
  const utils = trpc.useUtils();
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(0);

  useNotificationStream();

  const { abrir, ehDeOutraOrganizacao } = useAbrirNotificacao();
  // Mesmo escopo do sino — o número dele e a lista daqui precisam bater.
  const listQuery = trpc.notification.list.useQuery(
    {
      unreadOnly: filter === "unread",
      take: PAGE_SIZE,
      skip: page * PAGE_SIZE,
      escopo: "todas",
    },
    { placeholderData: keepPreviousData, refetchOnWindowFocus: true },
  );
  const unreadQuery = trpc.notification.unreadCount.useQuery({ escopo: "todas" }, {
    refetchOnWindowFocus: true,
  });

  const markAll = trpc.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notification.invalidate();
      toast.success("Todas marcadas como lidas.");
    },
  });

  const markAsRead = trpc.notification.markAsRead.useMutation({
    onMutate: async () => {
      await utils.notification.unreadCount.cancel();
      const prev = utils.notification.unreadCount.getData();
      if (prev) {
        utils.notification.unreadCount.setData(undefined, {
          count: Math.max(0, prev.count - 1),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) utils.notification.unreadCount.setData(undefined, ctx.prev);
    },
    onSettled: () => {
      utils.notification.invalidate();
    },
  });

  const remove = trpc.notification.delete.useMutation({
    onSuccess: () => {
      utils.notification.invalidate();
      toast.success("Notificação apagada.");
    },
  });

  const items = (listQuery.data?.items ?? []) as Array<{
    id: string;
    title: string;
    body: string;
    icon: string | null;
    accent: string | null;
    link: string | null;
    readAt: Date | string | null;
    createdAt: Date | string;
    organization?: { id: string; name: string; types: string[]; isPersonal: boolean } | null;
  }>;
  const total = listQuery.data?.total ?? 0;
  const unread = unreadQuery.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useTourDaPagina(NOTIFICACOES_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Notificações",
      subtitle:
        unread > 0
          ? `${unread} não ${unread === 1 ? "lida" : "lidas"}`
          : "Tudo em dia",
    },
    [unread],
  );

  const handleRemove = async (id: string) => {
    const ok = await confirm({
      title: "Apagar notificação?",
      description: "Essa ação é permanente.",
      confirmText: "Apagar",
      destructive: true,
    });
    if (ok) remove.mutate({ id });
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      {/* Header de ações. `quebra="nunca"`: os dois cabem lado a lado mesmo em 390px, e o
          `justify-between` de antes jogava o botão pra esquerda justamente quando quebrava. */}
      <FilterBar
        quebra="nunca"
        segmentacao={
          <div
            data-tour="notificacoes-filtros"
            className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-border dark:bg-muted/30"
          >
            {(["all", "unread"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setFilter(k);
                  setPage(0);
                }}
                className={cn(
                  "inline-flex h-9 items-center rounded px-3 text-xs font-medium transition-colors sm:h-8",
                  filter === k
                    ? "bg-white text-zinc-900 shadow-sm dark:bg-card dark:text-foreground"
                    : "text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {k === "all" ? "Todas" : `Não lidas${unread ? ` · ${unread}` : ""}`}
              </button>
            ))}
          </div>
        }
        acoes={
          unread > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate({ escopo: "todas" })}
              disabled={markAll.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[12px] font-semibold text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:opacity-60 dark:border-border dark:bg-card dark:text-primary dark:hover:bg-primary/10"
            >
              {markAll.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCheck className="h-3.5 w-3.5" />
              )}
              Marcar todas como lidas
            </button>
          )
        }
      />

      {/* Lista */}
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
        {listQuery.isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-primary" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-border">
            {items.map((n) => {
              const Icon = iconFor(n.icon);
              const accent = n.accent || "#2457ff";
              const isUnread = !n.readAt;
              const timeAgo = formatDistanceToNow(new Date(n.createdAt), {
                locale: ptBR,
                addSuffix: true,
              });
              return (
                <li
                  key={n.id}
                  className={cn(
                    "group relative flex items-start gap-3 px-4 py-4 transition-colors sm:px-6",
                    isUnread
                      ? "bg-brand-50/40 dark:bg-primary/5"
                      : "hover:bg-zinc-50 dark:hover:bg-muted/30",
                  )}
                >
                  <span
                    className={cn(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      isUnread ? "bg-brand-600 dark:bg-primary" : "bg-transparent",
                    )}
                  />
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    style={{
                      backgroundColor: `${accent}1a`,
                      color: accent,
                    }}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>

                  <button
                    type="button"
                    onClick={() => {
                      if (isUnread) markAsRead.mutate({ id: n.id });
                      abrir(n);
                    }}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p
                      className={cn(
                        "truncate text-[14px]",
                        isUnread
                          ? "font-bold text-zinc-900 dark:text-foreground"
                          : "font-semibold text-zinc-700 dark:text-foreground/80",
                      )}
                    >
                      {n.title}
                    </p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-zinc-600 dark:text-muted-foreground">
                      {n.body}
                    </p>
                    <p className="mt-1 text-[11px] font-medium text-zinc-400 dark:text-muted-foreground/80">
                      {ehDeOutraOrganizacao(n) && (
                        <span className="mr-1.5 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                          {n.organization!.name}
                        </span>
                      )}
                      {timeAgo}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRemove(n.id)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-zinc-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/30"
                    title="Apagar"
                    aria-label="Apagar notificação"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Paginação */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-muted-foreground">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} de{" "}
            {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
            >
              Anterior
            </button>
            <span className="px-2">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
              className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ filter }: { filter: Filter }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100/60 text-brand-500 dark:from-primary/10 dark:to-primary/5 dark:text-primary">
        <Bell className="h-8 w-8" />
      </div>
      <p className="text-sm font-semibold text-zinc-700 dark:text-foreground">
        {filter === "unread"
          ? "Nenhuma notificação não lida"
          : "Nenhuma notificação"}
      </p>
      <p className="max-w-sm px-6 text-xs leading-relaxed text-zinc-500 dark:text-muted-foreground">
        {filter === "unread"
          ? "Você está em dia. Volte aqui quando houver novidades."
          : "Quando pacientes se cadastrarem pelo link ou houver novidades, aparecerá aqui."}
      </p>
    </div>
  );
}
